import {
	Box,
	Button,
	Center,
	Collapse,
	Flex,
	IconButton,
	Spacer,
	Spinner,
	Stack,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useToast,
	Progress,
	Tag,
	TagLeftIcon,
	TagLabel,
	SimpleGrid,
	Image,
	Tooltip,
} from '@chakra-ui/react';

import ImageGallery from 'react-image-gallery';
import Dropzone from 'react-dropzone';

import {
	AddIcon,
	CloseIcon,
	RepeatClockIcon,
	CheckIcon,
	WarningIcon,
} from '@chakra-ui/icons';
import { BsEye } from 'react-icons/bs';
import { S3_ROUTE } from '../../../data/constants';
import {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';
import { uploadInmuebleFile } from '../../../services/inmuebles';
import {
	errorNotification,
	okNotification,
} from '../../../utils/Notificaciones';
import { useSelector } from 'react-redux';

const initialState = {
	queue: [], // [{ id, file, preview, status:'pending'|'uploading'|'done'|'error', progress:0, error:null }]
	uploading: false,
};

function reducer(state, action) {
	switch (action.type) {
		case 'ADD_FILES': {
			const now = Date.now();
			const next = action.files.map((file, idx) => ({
				id: `${now}-${idx}-${file.name}`,
				file,
				preview: URL.createObjectURL(file),
				status: 'pending',
				progress: 0,
				error: null,
			}));
			return { ...state, queue: [...next, ...state.queue] };
		}
		case 'SET_STATUS': {
			const { id, status, progress, error } = action;
			return {
				...state,
				queue: state.queue.map(item =>
					item.id === id
						? {
								...item,
								status: status ?? item.status,
								progress: progress ?? item.progress,
								error: error ?? item.error,
							}
						: item,
				),
			};
		}
		case 'REMOVE_ITEM': {
			return {
				...state,
				queue: state.queue.filter(i => i.id !== action.id),
			};
		}
		case 'CLEAR_DONE': {
			return {
				...state,
				queue: state.queue.filter(i => i.status !== 'done'),
			};
		}
		case 'SET_UPLOADING': {
			return { ...state, uploading: action.value };
		}
		default:
			return state;
	}
}

const ImagenesInmueble = ({
	imagenes,
	galleryImages,
	formData,
	refreshImagenes,
	fileLoading,
}) => {
	const toast = useToast();
	const { user } = useSelector(state => state.auth);

	const [mostrarSubirDocumento, setMostrarSubirDocumento] = useState(false);
	const [state, dispatch] = useReducer(reducer, initialState);
	const controllersRef = useRef({}); // id -> AbortController

	const hasImages = (imagenes?.length || 0) > 0;
	const memoGallery = useMemo(() => galleryImages, [galleryImages]);
	const memoImagenes = useMemo(() => imagenes || [], [imagenes]);

	// Limpieza de object URLs
	useEffect(() => {
		return () => {
			state.queue.forEach(i => URL.revokeObjectURL(i.preview));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDrop = useCallback(acceptedFiles => {
		if (!acceptedFiles?.length) return;
		dispatch({ type: 'ADD_FILES', files: acceptedFiles });
	}, []);

	const readFileAsBase64 = file =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = reader.result || '';
				const str = String(result);
				resolve(str.includes(',') ? str.split(',')[1] : str);
			};
			reader.onerror = () =>
				reject(reader.error || new Error('Error leyendo el archivo'));
			reader.readAsDataURL(file);
		});

	// Subida de un item utilizando la MISMA ruta y servicio existente
	const uploadItem = useCallback(
		async item => {
			const controller = new AbortController();
			controllersRef.current[item.id] = controller;

			dispatch({
				type: 'SET_STATUS',
				id: item.id,
				status: 'uploading',
				progress: 1,
				error: null,
			});

			try {
				// Convertimos el File -> Base64
				const base64 = await readFileAsBase64(item.file);

				// Llamamos a tu MISMO servicio/ruta:
				// POST /inmuebles/imagenes/:id  { filename, data }
				await uploadInmuebleFile(
					formData.uuid,
					item.file.name,
					base64,
					{
						onProgress: pct =>
							dispatch({
								type: 'SET_STATUS',
								id: item.id,
								progress: pct,
							}),
						signal: controller.signal,
					},
				);

				dispatch({
					type: 'SET_STATUS',
					id: item.id,
					status: 'done',
					progress: 100,
				});
			} catch (e) {
				const name = e?.name || '';
				if (name === 'CanceledError' || name === 'AbortError') {
					dispatch({
						type: 'SET_STATUS',
						id: item.id,
						status: 'error',
						error: 'Cancelado por el usuario',
					});
				} else {
					dispatch({
						type: 'SET_STATUS',
						id: item.id,
						status: 'error',
						error: e?.message || 'Error de subida',
					});
				}
			} finally {
				delete controllersRef.current[item.id];
			}
		},
		[formData?.uuid],
	);

	// Subida con concurrencia limitada a 3 sin tocar backend
	const startUpload = useCallback(async () => {
		const pending = state.queue.filter(
			f => f.status === 'pending' || f.status === 'error',
		);
		if (!pending.length) {
			errorNotification('No hay archivos pendientes', toast);
			return;
		}

		dispatch({ type: 'SET_UPLOADING', value: true });

		const concurrency = 3;
		const chunks = [];
		for (let i = 0; i < pending.length; i += concurrency) {
			chunks.push(pending.slice(i, i + concurrency));
		}

		try {
			for (const chunk of chunks) {
				await Promise.all(chunk.map(item => uploadItem(item)));
			}
			okNotification('Subidas finalizadas', toast);
			await refreshImagenes();
		} catch (e) {
			// Errores ya gestionados por item
		} finally {
			dispatch({ type: 'SET_UPLOADING', value: false });
		}
	}, [state.queue, toast, refreshImagenes, uploadItem]);

	const cancelItem = useCallback(id => {
		const controller = controllersRef.current[id];
		if (controller) controller.abort();
	}, []);

	const cancelAll = useCallback(() => {
		Object.values(controllersRef.current).forEach(c => c.abort());
	}, []);

	const clearDone = useCallback(() => {
		dispatch({ type: 'CLEAR_DONE' });
	}, []);

	const removeItem = useCallback(id => {
		dispatch({ type: 'REMOVE_ITEM', id });
	}, []);

	return (
		<Center>
			<Stack direction='row' spacing={4} w='100%'>
				{hasImages && (
					<Box w='55%'>
						<ImageGallery
							showBullets
							showIndex
							showNav
							infinite
							showPlayButton={false}
							lazyLoad
							items={memoGallery}
						/>
					</Box>
				)}

				<Box w={hasImages ? '45%' : '100%'}>
					<Flex gap={4} align='center'>
						<Text fontWeight='bold' fontSize='2xl' color='blue.800'>
							IMÁGENES DEL INMUEBLE
						</Text>
						<Spacer />
						<Tooltip label='Refrescar listado'>
							<Button
								rounded='none'
								colorScheme='blue'
								onClick={refreshImagenes}
								isDisabled={fileLoading || state.uploading}
							>
								<RepeatClockIcon />
							</Button>
						</Tooltip>
						<Tooltip
							label={
								mostrarSubirDocumento
									? 'Cerrar subidas'
									: 'Añadir imágenes'
							}
						>
							<Button
								rounded='none'
								isDisabled={fileLoading}
								colorScheme={
									mostrarSubirDocumento ? 'red' : 'blue'
								}
								onClick={() =>
									setMostrarSubirDocumento(v => !v)
								}
							>
								{mostrarSubirDocumento ? (
									<CloseIcon />
								) : (
									<AddIcon />
								)}
							</Button>
						</Tooltip>
					</Flex>

					<Collapse in={mostrarSubirDocumento} animateOpacity>
						<Center
							p={4}
							my={4}
							border='1px dashed'
							borderColor='gray.300'
							bgColor='blackAlpha.50'
							rounded='md'
							_hover={{ bg: 'blackAlpha.100' }}
						>
							<Dropzone
								onDrop={handleDrop}
								multiple
								accept={{
									'image/*': [
										'.jpg',
										'.jpeg',
										'.png',
										'.gif',
										'.webp',
										'.heic',
										'.heif',
									],
									'application/pdf': ['.pdf'],
								}}
								maxFiles={30}
								maxSize={20 * 1024 * 1024}
							>
								{({
									getRootProps,
									getInputProps,
									isDragActive,
									isDragReject,
								}) => (
									<Box
										{...getRootProps()}
										w='100%'
										textAlign='center'
										cursor='pointer'
										p={6}
									>
										<input {...getInputProps()} />
										<Text
											fontSize='lg'
											fontWeight='semibold'
										>
											{isDragActive
												? 'Suelta los archivos para subirlos…'
												: 'Arrastra y suelta archivos aquí'}
										</Text>
										<Text fontSize='sm' color='gray.600'>
											o haz clic para seleccionar
										</Text>
										{isDragReject && (
											<Text color='red.500' mt={2}>
												Algún archivo no es válido (tipo
												o tamaño).
											</Text>
										)}
									</Box>
								)}
							</Dropzone>
						</Center>

						{state.queue.length > 0 && (
							<Box
								p={3}
								border='1px solid'
								rounded='md'
								borderColor='gray.200'
								bg='white'
								shadow='sm'
							>
								<Flex gap={2} mb={3} wrap='wrap'>
									<Button
										size='sm'
										colorScheme='blue'
										onClick={startUpload}
										isDisabled={state.uploading}
									>
										Subir{' '}
										{
											state.queue.filter(
												i => i.status !== 'done',
											).length
										}{' '}
										archivo(s)
									</Button>
									<Button
										size='sm'
										colorScheme='red'
										variant='outline'
										onClick={cancelAll}
										isDisabled={!state.uploading}
									>
										Cancelar todo
									</Button>
									<Button
										size='sm'
										variant='ghost'
										onClick={clearDone}
										isDisabled={
											!state.queue.some(
												i => i.status === 'done',
											)
										}
									>
										Limpiar completados
									</Button>
								</Flex>

								<SimpleGrid
									columns={{ base: 1, md: 2 }}
									spacing={3}
								>
									{state.queue.map(item => (
										<Box
											key={item.id}
											p={2}
											border='1px solid'
											borderColor='gray.200'
											rounded='md'
										>
											<Flex align='center' gap={2} mb={2}>
												<Image
													src={item.preview}
													alt={item.file.name}
													boxSize='56px'
													objectFit='cover'
													rounded='md'
													fallback={
														<Box
															boxSize='56px'
															bg='gray.100'
															rounded='md'
														/>
													}
												/>
												<Box flex='1'>
													<Text
														fontSize='sm'
														noOfLines={1}
														title={item.file.name}
													>
														{item.file.name}
													</Text>
													<Text
														fontSize='xs'
														color='gray.600'
													>
														{(
															item.file.size /
															(1024 * 1024)
														).toFixed(2)}{' '}
														MB
													</Text>
												</Box>
												<IconButton
													aria-label='Eliminar de la cola'
													icon={<CloseIcon />}
													size={{
														base: 'sm',
														md: 'md',
													}}
													variant='ghost'
													onClick={() =>
														removeItem(item.id)
													}
													isDisabled={
														item.status ===
														'uploading'
													}
												/>
											</Flex>

											<Progress
												value={item.progress}
												size='sm'
												rounded='sm'
												mb={2}
												isIndeterminate={
													item.status ===
														'uploading' &&
													(item.progress === 0 ||
														item.progress === 1)
												}
											/>

											<Flex
												justify='space-between'
												align='center'
												gap={2}
											>
												{item.status === 'done' && (
													<Tag
														size='sm'
														colorScheme='green'
													>
														<TagLeftIcon
															as={CheckIcon}
														/>
														<TagLabel>
															Completado
														</TagLabel>
													</Tag>
												)}
												{item.status ===
													'uploading' && (
													<Tag
														size='sm'
														colorScheme='blue'
													>
														<TagLabel>
															Subiendo…{' '}
															{item.progress}%
														</TagLabel>
													</Tag>
												)}
												{item.status === 'error' && (
													<Tag
														size='sm'
														colorScheme='red'
													>
														<TagLeftIcon
															as={WarningIcon}
														/>
														<TagLabel>
															Error
														</TagLabel>
													</Tag>
												)}

												<Flex gap={2} ml='auto'>
													{item.status ===
														'error' && (
														<Button
															size='xs'
															variant='outline'
															onClick={() =>
																dispatch({
																	type: 'SET_STATUS',
																	id: item.id,
																	status: 'pending',
																	progress: 0,
																	error: null,
																})
															}
														>
															Reintentar
														</Button>
													)}
													{item.status ===
														'uploading' && (
														<Button
															size='xs'
															variant='outline'
															onClick={() =>
																cancelItem(
																	item.id,
																)
															}
														>
															Cancelar
														</Button>
													)}
												</Flex>
											</Flex>
										</Box>
									))}
								</SimpleGrid>
							</Box>
						)}
					</Collapse>

					<Box
						border='1px solid'
						rounded='md'
						p={4}
						my={4}
						w='100%'
						shadow='lg'
					>
						{fileLoading ? (
							<Box>
								<Center>
									<Spinner />
								</Center>
								<Text textAlign='center'>
									Cargando imágenes...
								</Text>
							</Box>
						) : (
							<Box>
								{memoImagenes.length > 0 ? (
									<TableContainer
										maxH='40rem'
										overflowY='auto'
									>
										<Table size='sm'>
											<Thead
												position='sticky'
												top={0}
												bg='white'
												zIndex={1}
											>
												<Tr>
													<Th>Nombre</Th>
													<Th>Fecha subida</Th>
													<Th textAlign='right'>
														Acciones
													</Th>
												</Tr>
											</Thead>
											<Tbody>
												{memoImagenes.map(
													(imagen, idx) => {
														const full = String(
															imagen.nombre ?? '',
														);
														const esLargo =
															full.length > 50;
														const nombre = esLargo
															? `${full.slice(0, 50)}…`
															: full;

														return (
															<Tr
																key={`${imagen.nombre}-${imagen.fecha}-${idx}`}
															>
																<Td
																	title={full}
																>
																	{nombre}
																</Td>
																<Td>
																	{new Date(
																		imagen.fecha,
																	).toLocaleDateString()}
																</Td>
																<Td>
																	<Flex
																		gap={2}
																		justify='flex-end'
																	>
																		{user &&
																			user.rol ===
																				'Gerente' && (
																				<IconButton
																					aria-label='Eliminar'
																					icon={
																						<CloseIcon />
																					}
																					size={{
																						base: 'sm',
																						md: 'md',
																					}}
																					rounded='none'
																					colorScheme='red'
																					// onClick={...}
																				/>
																			)}
																		<IconButton
																			aria-label='Ver'
																			icon={
																				<BsEye />
																			}
																			size={{
																				base: 'sm',
																				md: 'md',
																			}}
																			rounded='none'
																			colorScheme='blue'
																			onClick={() =>
																				window.open(
																					`${S3_ROUTE}/inmuebles/${formData.uuid}/${imagen.nombre}`,
																					'_blank',
																					'noopener,noreferrer',
																				)
																			}
																		/>
																	</Flex>
																</Td>
															</Tr>
														);
													},
												)}
											</Tbody>
										</Table>
									</TableContainer>
								) : (
									<Text>
										No hay imágenes para este inmueble.
									</Text>
								)}
							</Box>
						)}
					</Box>
				</Box>
			</Stack>
		</Center>
	);
};

export default ImagenesInmueble;
